import { GetStaticPropsContext } from 'next';

import { Box } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import {
  ExperienceAtfHeroSection,
  StaticExperienceAtfHeroSectionProps,
  transformStaticExperienceAtfHeroSectionProps,
} from '~/features/experience/components/experience-atf-hero-section';
import {
  ExperienceIndustryLeadersSection,
  StaticExperienceIndustryLeadersSectionProps,
  transformStaticExperienceIndustryLeadersSectionProps,
} from '~/features/experience/components/experience-industry-leaders-section';
import {
  ExperiencePlansSection,
  StaticExperiencePlansSectionProps,
  transformStaticExperiencePlansSectionProps,
} from '~/features/experience/components/experience-plans-section';
import {
  ExperienceSelectedProgramsSection,
  StaticExperienceSelectedProgramsSectionProps,
  transformStaticExperienceSelectedProgramsSectionProps,
} from '~/features/experience/components/experience-selected-programs-section';
import {
  ExperienceTestimonialsSection,
  StaticExperienceTestimonialsSectionProps,
  transformStaticExperienceTestimonialsSectionProps,
} from '~/features/experience/components/experience-testimonials-section';
import { MastersAIPromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

export default function ExperiencePage({
  page,
  staticExperienceAtfHeroSectionProps,
  staticExperienceTestimonialsSectionProps,
  staticExperiencePlansSectionProps,
  staticExperienceIndustryLeadersSectionProps,
  staticExperienceSelectedProgramsSectionProps,
}: {
  page: Page;
  staticExperienceAtfHeroSectionProps: StaticExperienceAtfHeroSectionProps;
  staticExperienceTestimonialsSectionProps: StaticExperienceTestimonialsSectionProps;
  staticExperiencePlansSectionProps: StaticExperiencePlansSectionProps;
  staticExperienceIndustryLeadersSectionProps: StaticExperienceIndustryLeadersSectionProps;
  staticExperienceSelectedProgramsSectionProps: StaticExperienceSelectedProgramsSectionProps;
}) {
  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: PageCategory.Experience,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <ExperienceAtfHeroSection {...staticExperienceAtfHeroSectionProps} />
        <Box paddingTop={{ base: '56px', md: '112px' }}>
          <ContentContainer contentMaxWidth='content-max-width-lg'>
            <MastersAIPromoCard
              onClick={() => {
                trackEvent({
                  type: 'click',
                  name: 'ExperiencePage CTA Clicked',
                  label: 'masters_entry_clicked',
                });
              }}
            />
          </ContentContainer>
        </Box>
        <ExperienceSelectedProgramsSection {...staticExperienceSelectedProgramsSectionProps} />
        <ExperienceIndustryLeadersSection {...staticExperienceIndustryLeadersSectionProps} />
        <ExperienceTestimonialsSection {...staticExperienceTestimonialsSectionProps} />
        <ExperiencePlansSection {...staticExperiencePlansSectionProps} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page] = await Promise.all([getPage('/experience')]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const staticExperienceAtfHeroSectionProps = transformStaticExperienceAtfHeroSectionProps();
  const staticExperienceTestimonialsSectionProps = transformStaticExperienceTestimonialsSectionProps();
  const staticExperiencePlansSectionProps = transformStaticExperiencePlansSectionProps();
  const staticExperienceIndustryLeadersSectionProps = await transformStaticExperienceIndustryLeadersSectionProps();
  const staticExperienceSelectedProgramsSectionProps = await transformStaticExperienceSelectedProgramsSectionProps();

  return {
    props: {
      page,
      staticExperienceAtfHeroSectionProps,
      staticExperienceTestimonialsSectionProps,
      staticExperiencePlansSectionProps,
      staticExperienceIndustryLeadersSectionProps,
      staticExperienceSelectedProgramsSectionProps,
    },
  };
}
